// Postgres-backed Read API helper (migrating active_users_* reads off Firestore).
// Each function calls the activeUsersReadApi Cloud Function with the caller's Firebase
// ID token; the function resolves the user's company from RTDB and returns ONLY that
// company's data. All functions return null on error/no-auth so callers can fall back
// to their existing zero-handling.
import firebase from 'firebase/app';
import 'firebase/auth';
import axios from 'axios';
import { decodeSessionUser } from './core/cacheService';

// The app logs in via signInWithCustomToken, so the Firebase ID token carries no
// `email` claim. The Read APIs resolve the caller's company from the email, so we pass
// the logged-in email (from the persisted session) as ?caller=. The token is still
// verified server-side — this only supplies the identity the token omits.
function callerEmail(): string | null {
  try {
    const u = decodeSessionUser(localStorage.getItem('userData'));
    return u && u.email ? String(u.email) : null;
  } catch (e) {
    return null;
  }
}

// axios instance that stamps ?caller=<email> onto every PG Read API request so the
// backend can resolve the caller's company (the token alone can't, no email claim).
const pgClient = axios.create();
pgClient.interceptors.request.use((config: any) => {
  const caller = callerEmail();
  if (caller) config.params = { ...(config.params || {}), caller };
  return config;
});

const PG_READ_API = 'https://us-central1-people2-admin.cloudfunctions.net/activeUsersReadApi';
const PG_READ_API_MESSAGES = 'https://us-central1-people2-admin.cloudfunctions.net/messagesReadApi';
const PG_READ_API_RT = 'https://us-central1-people2-admin.cloudfunctions.net/runningTrackReadApi';

async function authHeaders(): Promise<any> {
  const cu = firebase.auth().currentUser;
  if (!cu) return null;
  const token = await cu.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

// monthly/weekly single-period distinct count -> { active_users } | null
// `company` is the company being VIEWED (rootState.authen.user.company). The backend
// honours it for the admin company (cross-company view) and ignores it for normal users
// (forced to their own company), so passing it is always safe and restores the old
// Firestore behaviour of querying by the client's selected company.
export async function pgPeriodCount(kind: string, period: string, company?: string): Promise<any> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const paramKey = kind === 'monthly' ? 'ym' : 'woy';
    const params: any = {};
    params[paramKey] = period;
    if (company) params.company = company;
    const res = await pgClient.get(`${PG_READ_API}/${kind}`, { params, headers });
    return { active_users: Number(res.data.activeUsers) || 0 };
  } catch (error) {
    console.error('pgPeriodCount failed:', kind, period, error);
    return null;
  }
}

// distinct monthly active count for each month in [from, to] -> { 'YYYY-MM': activeUsers } | null
// (replaces range reads of the Firestore active_users monthly collection)
export async function pgMonthlyRange(from: string, to: string, company?: string): Promise<{ [ym: string]: number } | null> {
  try {
    const start = new Date(from);
    const end = new Date(to);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    const months: string[] = [];
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cur <= last) {
      months.push(cur.getFullYear() + '-' + String(cur.getMonth() + 1).padStart(2, '0'));
      cur.setMonth(cur.getMonth() + 1);
    }
    const results = await Promise.all(months.map((ym) => pgPeriodCount('monthly', ym, company)));
    const out: { [ym: string]: number } = {};
    months.forEach((ym, i) => { out[ym] = results[i] ? Number(results[i].active_users) || 0 : 0; });
    return out;
  } catch (error) {
    console.error('pgMonthlyRange failed:', from, to, error);
    return null;
  }
}

// distinct active-user count over [from,to] (YYYY-MM-DD) -> number | null
export async function pgActiveCount(from: string, to: string, company?: string): Promise<number | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const params: any = { from, to };
    if (company) params.company = company;
    const res = await pgClient.get(`${PG_READ_API}/active-count`, { params, headers });
    return Number(res.data.activeUsers) || 0;
  } catch (error) {
    console.error('pgActiveCount failed:', from, to, error);
    return null;
  }
}

// per-day active users over [from,to] -> [{ date, users_qty, users[] }] | null
// (replaces summary_active_users docs read per day)
export async function pgSummary(from: string, to: string, company?: string): Promise<any[] | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const params: any = { from, to };
    if (company) params.company = company;
    const res = await pgClient.get(`${PG_READ_API}/summary`, { params, headers });
    return (res.data && res.data.days) ? res.data.days : [];
  } catch (error) {
    console.error('pgSummary failed:', from, to, error);
    return null;
  }
}

// per-day team (hierarchical rollup) or cohort active users over [from,to]
// -> [{ date, usersActive, usersTotal, users[] }] | null
// (replaces active_users_teams docs read per day). opts.company = company being viewed.
export async function pgTeams(opts: { team?: string; cohort?: string; from: string; to: string; company?: string }): Promise<any[] | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const params: any = { from: opts.from, to: opts.to };
    if (opts.team) params.team = opts.team;
    if (opts.cohort) params.cohort = opts.cohort;
    if (opts.company) params.company = opts.company;
    const res = await pgClient.get(`${PG_READ_API}/teams`, { params, headers });
    return (res.data && res.data.days) ? res.data.days : [];
  } catch (error) {
    console.error('pgTeams failed:', opts, error);
    return null;
  }
}

// company-wide breakdown: every team (kind='teams') or every cohort (kind='cohorts').
// -> { <id>: [{ date, usersActive, usersTotal, users[] }] } (sparse: only ids active in range) | null
export async function pgTeamsAll(kind: 'teams' | 'cohorts', from: string, to: string, company?: string): Promise<any> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const params: any = { all: kind, from, to };
    if (company) params.company = company;
    const res = await pgClient.get(`${PG_READ_API}/teams`, { params, headers });
    if (!res.data) return null;
    return kind === 'teams' ? (res.data.teams || {}) : (res.data.cohorts || {});
  } catch (error) {
    console.error('pgTeamsAll failed:', kind, from, to, error);
    return null;
  }
}

// ============================================================================
// Phase 2 running_track migration: per-period leaderboards + house roll-ups served
// from runningTrackReadApi (PG-backed). Each helper returns the parsed entries OR
// null on error/no-auth so callers can fall back to their existing Firestore read.
// Entry rows are already enriched server-side with `name`/`color` (users) or
// `house_name`/`color` (houses) via RTDB join, so consumers don't need a second
// identity-lookup pass.
// ============================================================================

// /daily?date=YYYY-MM-DD -> [{ email, name, house, color, distance, rank }] | null
export async function pgRtDaily(date: string, limit = 1000): Promise<any[] | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const res = await pgClient.get(`${PG_READ_API_RT}/daily`, { params: { date, limit }, headers });
    return (res.data && Array.isArray(res.data.entries)) ? res.data.entries : [];
  } catch (error) {
    console.error('pgRtDaily failed:', date, error);
    return null;
  }
}

// /weekly?start&end -> [{ email, name, house, color, distance, rank }] | null
export async function pgRtWeekly(start: string, end: string, limit = 1000): Promise<any[] | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const res = await pgClient.get(`${PG_READ_API_RT}/weekly`, { params: { start, end, limit }, headers });
    return (res.data && Array.isArray(res.data.entries)) ? res.data.entries : [];
  } catch (error) {
    console.error('pgRtWeekly failed:', start, end, error);
    return null;
  }
}

// /monthly?ym -> [{ email, name, house, color, distance, rank }] | null
export async function pgRtMonthly(ym: string, limit = 1000): Promise<any[] | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const res = await pgClient.get(`${PG_READ_API_RT}/monthly`, { params: { ym, limit }, headers });
    return (res.data && Array.isArray(res.data.entries)) ? res.data.entries : [];
  } catch (error) {
    console.error('pgRtMonthly failed:', ym, error);
    return null;
  }
}

// /house?ym -> [{ house, house_name, color, distance, members, rank }] | null
export async function pgRtHouse(ym: string): Promise<any[] | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const res = await pgClient.get(`${PG_READ_API_RT}/house`, { params: { ym }, headers });
    return (res.data && Array.isArray(res.data.houses)) ? res.data.houses : [];
  } catch (error) {
    console.error('pgRtHouse failed:', ym, error);
    return null;
  }
}

// /house/history?from&to (YYYY-MM) -> { 'YYYY-MM': [{ house, distance }] } | null
export async function pgRtHouseHistory(from: string, to: string): Promise<{ [ym: string]: any[] } | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const res = await pgClient.get(`${PG_READ_API_RT}/house/history`, { params: { from, to }, headers });
    return (res.data && res.data.months) ? res.data.months : {};
  } catch (error) {
    console.error('pgRtHouseHistory failed:', from, to, error);
    return null;
  }
}

// /monthly/history?from&to (YYYY-MM) -> { 'YYYY-MM': totalDistance } | null
// Company TOTAL per month over ALL runners (house-less rows included) — unlike
// /house/history it also covers months that predate house-stamping on day docs
// (~2026-02), so the Distance Traveled chart can render full backfilled history.
export async function pgRtMonthlyHistory(from: string, to: string): Promise<{ [ym: string]: number } | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const res = await pgClient.get(`${PG_READ_API_RT}/monthly/history`, { params: { from, to }, headers });
    return (res.data && res.data.months) ? res.data.months : {};
  } catch (error) {
    console.error('pgRtMonthlyHistory failed:', from, to, error);
    return null;
  }
}

// /day-range?start&end -> [{ date, email, house, distance, distanceMain }] | null
export async function pgRtDayRange(start: string, end: string): Promise<any[] | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const res = await pgClient.get(`${PG_READ_API_RT}/day-range`, { params: { start, end }, headers });
    return (res.data && Array.isArray(res.data.rows)) ? res.data.rows : [];
  } catch (error) {
    console.error('pgRtDayRange failed:', start, end, error);
    return null;
  }
}

// ============================================================================
// Phase 2 messages migration: per-manager-per-day aggregates from messagesReadApi
// (covers getrespornse + monthly trend in insights-managers).
// GET /manager-metrics?days=N -> [{ manager, day, total_response, total_res,
//   total_res_time, response_hours_sum, responses }] | null on error/no-auth.
export async function pgManagerMetrics(days: number): Promise<any[] | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const res = await pgClient.get(`${PG_READ_API_MESSAGES}/manager-metrics`, { params: { days }, headers });
    return (res.data && Array.isArray(res.data.rows)) ? res.data.rows : [];
  } catch (error) {
    console.error('pgManagerMetrics failed:', days, error);
    return null;
  }
}
